(() => {
  'use strict';

  const OPEN_CLASS = 'zyv-filter-drawer-open';
  const BODY_CLASS = 'zyv-filter-is-open';
  const DRAWER_MEDIA = window.matchMedia('(max-width: 1199px)');
  let active = null;
  let lastTrigger = null;

  const getContext = () => {
    const root = document.querySelector('.template-collection #main-collection-filters');
    if (!root) return null;

    const wrap = root.querySelector('.sidebar-filter-wrap');
    const inner = wrap?.querySelector('.shop-sidebar-inner');
    const overlay = root.querySelector('.zyv-drawer-overlay');
    const close = wrap?.querySelector('.close-filter-sidebar');
    const withoutSidebar =
      inner?.classList.contains('shop-sidebar-without') ||
      root.closest('.product-without-sidebar') !== null;

    if (!wrap || !inner || !overlay) return null;
    return { root, wrap, inner, overlay, close, withoutSidebar };
  };

  const isDrawerMode = (context) =>
    DRAWER_MEDIA.matches || context.withoutSidebar;

  const setTriggerState = (expanded) => {
    document.querySelectorAll('.template-collection .filter-button').forEach((button) => {
      const label = button.querySelector('span');
      if (label) label.textContent = 'Filter';
      button.setAttribute('aria-label', expanded ? 'Close filters' : 'Open filters');
      button.setAttribute('aria-expanded', String(expanded));
      button.setAttribute('aria-haspopup', 'dialog');

      const context = getContext();
      if (context?.wrap.id) button.setAttribute('aria-controls', context.wrap.id);
    });
  };

  const clearLegacyState = (context) => {
    context.inner.classList.remove('active-parent');
    context.inner.style.removeProperty('left');
    context.inner.style.removeProperty('inset-inline-start');
    context.overlay.classList.remove('zyv-drawer-open');
    context.wrap.classList.remove('zyv-open', 'active');
    document.querySelectorAll('.mm-fullscreen-bg').forEach((overlay) => {
      overlay.classList.remove('active');
    });
  };

  const openDrawer = (trigger) => {
    const context = getContext();
    if (!context || !isDrawerMode(context)) return;

    active = context;
    lastTrigger = trigger || document.activeElement;
    clearLegacyState(context);

    context.wrap.classList.add(OPEN_CLASS);
    context.overlay.classList.add(OPEN_CLASS);
    context.overlay.setAttribute('aria-hidden', 'false');
    context.inner.setAttribute('role', 'dialog');
    context.inner.setAttribute('aria-modal', 'true');
    context.inner.setAttribute('aria-label', 'Filter products');
    document.documentElement.classList.add(BODY_CLASS);
    document.body.classList.add(BODY_CLASS);
    setTriggerState(true);

    window.requestAnimationFrame(() => {
      (context.close || context.wrap.querySelector('button, input, select, a[href]'))?.focus({
        preventScroll: true
      });
    });
  };

  const closeDrawer = (restoreFocus = true) => {
    const context = active || getContext();
    if (!context) return;

    context.wrap.classList.remove(OPEN_CLASS);
    context.overlay.classList.remove(OPEN_CLASS);
    context.overlay.setAttribute('aria-hidden', 'true');
    context.inner.removeAttribute('aria-modal');
    document.documentElement.classList.remove(BODY_CLASS);
    document.body.classList.remove(BODY_CLASS);
    setTriggerState(false);
    active = null;

    if (restoreFocus && lastTrigger instanceof HTMLElement) {
      window.requestAnimationFrame(() => lastTrigger.focus({ preventScroll: true }));
    }
  };

  const handleClick = (event) => {
    const filterButton = event.target.closest?.('.template-collection .filter-button');
    if (filterButton) {
      const context = getContext();
      if (!context || !isDrawerMode(context)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (context.wrap.classList.contains(OPEN_CLASS)) closeDrawer(false);
      else openDrawer(filterButton);
      return;
    }

    const closeButton = event.target.closest?.(
      '.template-collection .close-filter-sidebar'
    );
    const overlay = event.target.closest?.(
      '.template-collection .zyv-drawer-overlay'
    );

    if (closeButton || overlay) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeDrawer();
    }
  };

  const handleKeydown = (event) => {
    if (event.key === 'Escape' && active) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab' || !active) return;
    const focusable = [...active.wrap.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    )].filter((element) => element.offsetParent !== null);

    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const boot = () => {
    const context = getContext();
    if (!context) return;

    clearLegacyState(context);
    context.wrap.classList.remove(OPEN_CLASS);
    context.overlay.classList.remove(OPEN_CLASS);
    context.overlay.setAttribute('aria-hidden', 'true');
    setTriggerState(false);

    const handleViewportChange = () => {
      closeDrawer(false);
      const refreshed = getContext();
      if (refreshed) clearLegacyState(refreshed);
    };

    if (DRAWER_MEDIA.addEventListener) {
      DRAWER_MEDIA.addEventListener('change', handleViewportChange);
    } else {
      DRAWER_MEDIA.addListener(handleViewportChange);
    }
  };

  document.addEventListener('click', handleClick, true);
  document.addEventListener('keydown', handleKeydown, true);

  if (document.readyState === 'complete') {
    window.requestAnimationFrame(boot);
  } else {
    window.addEventListener(
      'DOMContentLoaded',
      () => window.requestAnimationFrame(boot),
      { once: true }
    );
  }
})();